import React, { useEffect, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import CircularProgress from '@mui/material/CircularProgress';
import {
    BaseSelection,
    createEditor,
    Descendant,
    Editor,
    Element,
    Point,
    Range,
    Text,
    Transforms,
} from 'slate';
import { withHistory } from 'slate-history';
import { ReactEditor, Slate, withReact } from 'slate-react';

import BasicSection from '../../../components/display/layout/basicSection';
// components
import FormulaError from '../../../components/errors/FormulaErrorBoundary';
import { FormulaEditorToolbarControls } from './assistant/components/FormulaEditorToolbar';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

// plugins
import withAampe from '../../../editors/PushNotificationEditor/plugins';

import {
    AttributePlaceholderElement,
    ButtonActionElement,
    ButtonElement,
    ButtonTextElement,
    ButtonType,
    CMSPlaceholderElement,
    DatasetElement,
    EditorType,
    ElementKind,
    ImageElement,
    ImageVariantElement,
    LinkElement,
    primaryButton,
    secondaryButton,
    SectionElement,
    SurfaceButtonElement,
    TextElement,
    VariantElement,
} from '../../../models/editor.model';
// models
import {
    FormulaDetailModel,
    MaxCharacterModel,
} from '../../../models/formula.model';

import { FormulaCreateType } from '../FormulaCreateMainV2';
import PnEditor from './PnEditor';
import SidebarMain from './SidebarMain';
import { useEditorFormula } from './useEditorFormula';

declare module 'slate' {
    interface CustomTypes {
        Editor: EditorType;
        Element:
            | SectionElement
            | VariantElement
            | DatasetElement
            | CMSPlaceholderElement
            | ButtonActionElement
            | ButtonTextElement
            | ButtonElement
            | ImageElement
            | ImageVariantElement
            | LinkElement
            | SurfaceButtonElement
            | AttributePlaceholderElement;
        Text: TextElement;
    }
}

interface FormulaEditorMainProps {
    data?: FormulaDetailModel;
    initialFormula: Descendant[];
    createOrUpdateFormula: FormulaCreateType;
    maxCharacterCounts?: MaxCharacterModel;
    className?: string;
    hideToggleButtons?: boolean;
    toolbarControls?: FormulaEditorToolbarControls[];
    reinitializeMessageContent?: number;
    isContentLoading?: boolean;
    hasWriteAccess: boolean;
}

export enum ToggleButtonValues {
    VARIATION = 'VARIATION',
    PREVIEW = 'PREVIEW',
    TEST_MESSAGE = 'TEST_MESSAGE',
    WHATSAPP_TEMPLATE = 'WHATSAPP_TEMPLATE',
    CONTENT_TAG = 'CONTENT_TAG',
}

const FormulaEditorMain: React.FC<
    React.PropsWithChildren<FormulaEditorMainProps>
> = ({
    data,
    initialFormula,
    createOrUpdateFormula,
    maxCharacterCounts,
    className,
    hideToggleButtons,
    toolbarControls,
    reinitializeMessageContent,
    isContentLoading,
    hasWriteAccess,
}) => {
    const { formula, setFormula } = useEditorFormula({
        initialFormula,
        reinitializeMessageContent,
    });
    const [currentSelection, setCurrentSelection] = useState<Range>();
    const [alternateCreateError, setAlternateCreateError] = useState<
        string | null
    >(null);

    const [editor] = useState<ReactEditor>(() =>
        withAampe(withReact(withHistory(createEditor()))),
    );

    // to fix the inconsistency of editor.children.
    editor.children = formula;

    const [selectedElement, setSelectedElement] = useState<{
        [key: string]: any;
    }>();

    const formulaId = data?.id;

    const isTextSelected = editor.selection
        ? Range.isExpanded(editor.selection)
        : false;

    const [isVariationTabOpened, setIsVariationTabOpened] = useState(
        selectedElement &&
            ![ElementKind.cms_placeholder, ElementKind.link].includes(
                selectedElement.kind,
            )
            ? ToggleButtonValues.VARIATION
            : ToggleButtonValues.PREVIEW,
    );

    useEffect(() => {
        setIsVariationTabOpened(
            selectedElement &&
                ![
                    ElementKind.cms_placeholder,
                    ElementKind.dataset,
                    ElementKind.link,
                ].includes(selectedElement.kind)
                ? ToggleButtonValues.VARIATION
                : ToggleButtonValues.PREVIEW,
        );
    }, [isTextSelected, selectedElement]);

    const lastFormulaElement = useMemo(
        () => formula?.[formula?.length - 1] ?? null,
        [formula],
    );

    const editDataset = (id: string, edits: any) => {
        const [result] = Editor.nodes(editor, {
            match: (node) =>
                !Editor.isEditor(node) &&
                Element.isElement(node) &&
                node.kind === ElementKind.dataset &&
                node.id === id,
            at: [],
        });
        Transforms.setNodes(editor, { ...edits }, { at: result[1] });
        setSelectedElement({
            ...selectedElement,
            ...selectedElement?.data,
            ...edits,
        });
    };

    const editCMSPlaceholder = (id: string, cmsPlaceholderId: string) => {
        const [result] = Editor.nodes(editor, {
            match: (node) =>
                !Editor.isEditor(node) &&
                Element.isElement(node) &&
                node.kind === ElementKind.cms_placeholder &&
                node.id === id,
            at: [],
        });
        Transforms.setNodes(editor, { cmsPlaceholderId }, { at: result[1] });
        setSelectedElement({
            ...selectedElement,
            cmsPlaceholderId,
        });
    };

    const editUserAttributePlaceholder = (
        id: string,
        defaultValue: string,
        attributeName: string,
    ) => {
        const [result] = Editor.nodes(editor, {
            match: (node) =>
                !Editor.isEditor(node) &&
                Element.isElement(node) &&
                node.kind === ElementKind.attributePlaceholder &&
                node.id === id,
            at: [],
        });
        Transforms.setNodes(
            editor,
            { defaultValue, attributeName },
            { at: result[1] },
        );
        setSelectedElement({
            ...selectedElement,
            attributeName,
            defaultValue,
        });
    };

    const editVariantType = (
        id: string,
        variantType: string,
        componentId: string,
    ) => {
        const [result] = Editor.nodes(editor, {
            match: (node) =>
                !Editor.isEditor(node) &&
                Element.isElement(node) &&
                node.kind === ElementKind.variant &&
                node.id === id,
            at: [],
        });
        Transforms.setNodes(
            editor,
            { variantType, componentId },
            { at: result[1] },
        );
        setSelectedElement({ ...selectedElement, variantType, componentId });
    };

    const handleValueChange = (newValue: Descendant[]) => {
        setFormula(newValue);
        createOrUpdateFormula({
            structure: newValue,
        });
    };

    const handleSelection = (currentLocation: Point) => {
        const [result] = Editor.nodes(editor, {
            match: (node) =>
                !Editor.isEditor(node) &&
                Element.isElement(node) &&
                (node.kind === ElementKind.variant ||
                    node.kind === ElementKind.dataset ||
                    node.kind === ElementKind.cms_placeholder ||
                    node.kind === ElementKind.link) &&
                Boolean(node.id),
            at: currentLocation,
        });

        if (
            result &&
            result[0] &&
            !Editor.isEditor(result[0]) &&
            !Text.isText(result[0])
        ) {
            const node = result[0];

            if (node.kind === ElementKind.variant) {
                const { kind, variantType, id } = node;
                setSelectedElement({ kind, variantType, elementId: id });
            } else if (node.kind === ElementKind.dataset) {
                const { datasetId, datasetField, kind, variantType, id } = node;
                setSelectedElement({
                    kind,
                    variantType,
                    elementId: id,
                    datasetId,
                    datasetField,
                });
            } else if (node.kind === ElementKind.cms_placeholder) {
                const { kind, id, cmsPlaceholderId } = node;
                setSelectedElement({
                    kind,
                    elementId: id,
                    cmsPlaceholderId,
                });
            } else if (node.kind === ElementKind.link) {
                const { kind, children, id } = node;
                setSelectedElement({ kind, children, elementId: id });
            }
        } else {
            setSelectedElement(undefined);
        }
    };

    const handleAddButton = () => {
        if ((lastFormulaElement as Element).kind !== ElementKind.button) {
            setFormula((formula) => [...formula, primaryButton]);
        } else if (
            (lastFormulaElement as ButtonElement).buttonType !==
            ButtonType.secondary
        ) {
            setFormula((formula) => [...formula, secondaryButton]);
        }
    };

    const handleRemoveButton = (buttonType: ButtonType) => {
        setFormula((currentFormula) => {
            const newFormula = currentFormula.filter(
                (element) =>
                    !((element as ButtonElement).buttonType === buttonType),
            );
            createOrUpdateFormula({
                structure: newFormula,
            });
            return newFormula;
        });
    };

    const handleUpdateStructure = (newValue: Descendant[]) => {
        setFormula(newValue);
    };

    const formulaHasSecondaryButton = useMemo<boolean>(
        () =>
            (lastFormulaElement as ButtonElement)?.buttonType ===
            ButtonType.secondary,
        [lastFormulaElement],
    );

    const handleSelectionChange = (selection: BaseSelection) => {
        if (selection) {
            const { focus } = selection;
            setCurrentSelection(selection);
            handleSelection(focus);
        }
    };

    useEffect(() => {
        if (reinitializeMessageContent) {
            setSelectedElement(undefined);
            setCurrentSelection(undefined);
            setAlternateCreateError(null);
        }
    }, [reinitializeMessageContent]);

    if (isContentLoading) {
        return (
            <Container
                className={className}
                style={{ justifyContent: 'center', alignItems: 'center' }}
            >
                <CircularProgress />
            </Container>
        );
    }

    return (
        <>
            <Sentry.ErrorBoundary fallback={FormulaError}>
                <Slate
                    editor={editor}
                    initialValue={formula}
                    onValueChange={handleValueChange}
                    onSelectionChange={handleSelectionChange}
                >
                    <BasicSection
                        style={{
                            padding: aampeTheme.spacing(0),
                            width: '100%',
                            position: 'relative',
                        }}
                    >
                        <Container className={className}>
                            <PnEditor
                                formulaId={formulaId}
                                setSelectedElement={setSelectedElement}
                                selectedElement={selectedElement}
                                currentSelection={currentSelection}
                                handleAddButton={handleAddButton}
                                formulaHasSecondaryButton={
                                    formulaHasSecondaryButton
                                }
                                handleRemoveButton={handleRemoveButton}
                                createFormula={createOrUpdateFormula}
                                maxCharacterCounts={maxCharacterCounts}
                                editCMSPlaceholder={editCMSPlaceholder}
                                editUserAttributePlaceholder={
                                    editUserAttributePlaceholder
                                }
                                setAlternateCreateError={
                                    setAlternateCreateError
                                }
                                handleUpdateStructure={handleUpdateStructure}
                                status={data?.status}
                                editDataset={editDataset}
                                toolbarControls={toolbarControls}
                                hasWriteAccess={hasWriteAccess}
                            />
                            <SidebarMain
                                isVariationTab={isVariationTabOpened}
                                setIsVariationTab={setIsVariationTabOpened}
                                formulaId={formulaId}
                                selectedElement={selectedElement!}
                                editVariantType={editVariantType}
                                currentSelection={currentSelection!}
                                setSelectedElement={setSelectedElement}
                                alternateCreateError={alternateCreateError}
                                setAlternateCreateError={
                                    setAlternateCreateError
                                }
                                variantId={selectedElement?.elementId}
                                hideToggleButtons={hideToggleButtons}
                                hasWriteAccess={hasWriteAccess}
                            />
                        </Container>
                    </BasicSection>
                </Slate>
            </Sentry.ErrorBoundary>
        </>
    );
};

const Container = styled.div(() => ({
    padding: aampeTheme.spacing(4),
    display: 'flex',
    gap: aampeTheme.spacing(2),
    justifyContent: 'space-between',
    minHeight: '750px',
    marginBottom: aampeTheme.spacing(3),
    position: 'relative',
}));

export default FormulaEditorMain;
